import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router';

import {
    Back,
    Button,
    Checkbox,
    ColContainer,
    EMPTY_IMAGE,
    LoadingSpinner,
    RowContainer,
    SelectList,
    Typography,
} from '../../components/common';
import toast from '../../components/common/toast';
import IconComponent from '../../components/Icon';
import AddUserToInstanceModal from '../../components/User/Modals/AddUserToInstanceModal';
import { useGetUser } from '../../contexts/user';
import { GetRoles } from '../../services/Api/roles/useApiRoles';
import { GetSchedules } from '../../services/Api/schedules/useApiSchedules';
import {
    CreateInstanceUser,
    GetUser,
    UpdateInstanceUser,
    DeleteInstanceUser,
    UpdateUser,
    DeleteUser,
} from '../../services/Api/users/useApiUser';
import useUserPageState from './userPageState';
import Avatar from '../../components/User/Avatar';
import Generic from '../error/Generic';

export default function User() {
    const {
        userInfo,
        setUserInfo,
        setRoutingProfile,
        setSecurityProfile,
        setAfterCallAllowance,
        setAutoAccept,
        setPermission,
        setSchedule,
        parseName,
        removeInstance,
        addInstance,
    } = useUserPageState();
    const [savedStates, setSavedStates] = useState([]);
    const [loadingStates, setLoadingStates] = useState({
        initial: true,
        user: false,
        instance: false,
    });
    const [userLoadError, setUserLoadError] = useState(false);
    const [schedules, setSchedules] = useState([]);
    const [role, setRole] = useState();
    const [roles, setRoles] = useState();
    const router = useHistory();

    const [modalControls, setModalControls] = useState({ open: false, currentInstance: {} });
    const { id } = useParams();

    const { userDetails, appUserDetails, hasRolePermission, token } = useGetUser();
    const isMe = id.toLowerCase() === userDetails.username.toLowerCase();

    const canAddUser = hasRolePermission('ac');
    const iAmSuperior = appUserDetails?.roleid <= userInfo?.roleid;
    const canEditUser = hasRolePermission('ae') && !isMe && iAmSuperior;
    const canDeleteUser = canEditUser && hasRolePermission('ad') && !isMe && iAmSuperior;

    useEffect(() => {
        setLoadingStates({ initial: true, user: false, instance: false });

        GetSchedules()
            .then((response) => {
                if (response.data) {
                    setSchedules(response.data);
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error();
            });

        GetUser({ email: id })
            .then((response) => {
                if (response.data) {
                    setUserInfo(response.data);
                    setUserLoadError(!response.data.email);
                    setRole({ label: '', value: response.data.roleid });
                    setLoadingStates({ initial: false, user: false, instance: false });
                }
            })
            .catch((err) => {
                toast.error();
                console.error(err);
                setUserLoadError(true);
                setLoadingStates({ initial: false, user: false, instance: false });
            });

        GetRoles()
            .then((response) => {
                const mappedRoles = response.data.map((role) => {
                    return {
                        label: role.name,
                        value: role.id,
                        disabled: role.id < appUserDetails?.roleid,
                    };
                });
                setRoles(mappedRoles);
            })
            .catch((err) => {
                console.error(err);
                toast.error();
            });
    }, [id, token, appUserDetails, setUserInfo]);

    // Required for proper mapping and display of the user role on initial load.
    useEffect(() => {
        if (roles && role) {
            setRole(roles?.find((r) => r.value === role.value));
        }
    }, [role, roles]);

    const onSaveInstanceSettings = (instance) => {
        setLoadingStates({ initial: false, user: false, instance: true });
        UpdateInstanceUser(
            { instanceId: instance.instanceid },
            {
                ScheduleId: instance.scheduleid,
                UserId: instance.user.Id,
                PhoneConfig: instance.user.PhoneConfig,
                RoutingProfileId: instance.user.RoutingProfileId,
                SecurityProfileIds: instance.user.SecurityProfileIds,
                IdentityInfo: { ...instance.user.IdentityInfo, Email: userInfo.email },
                Username: userInfo.email,
            }
        )
            .then((response) => {
                if (response.data) {
                    toast.success(`User Updated`);
                }
                setLoadingStates({ initial: false, user: false, instance: false });
            })
            .catch((err) => {
                toast.error();
                console.error(err);
                setLoadingStates({ initial: false, user: false, instance: false });
            });
    };

    const onDeleteUser = () => {
        const isInstanceMember = userInfo.instances.some((i) => !!i.user);
        if (isInstanceMember) {
            toast.error('Cannot remove a user who belongs to a Connect Instance.');
            return;
        }

        DeleteUser({ email: userInfo.email })
            .then((response) => {
                if (response && response.data) {
                    toast.success('User Deleted');
                    router.goBack();
                }
            })
            .catch((e) => {
                toast.error();
            });
    };

    const onDeleteInstanceUser = (instance) => {
        const instanceId = instance.instanceid;
        const userId = instance?.user?.Id || instance?.User?.UserId;
        DeleteInstanceUser({ instanceId }, { UserId: userId })
            .then((response) => {
                if (response && response.data) {
                    toast.success('User Removed from Instance');
                    removeInstance(instanceId);
                }
            })
            .catch((e) => {
                toast.error();
            });
    };

    const onSaveUserInfo = () => {
        setLoadingStates({ initial: false, user: true });
        UpdateUser(
            { email: userInfo.email },
            {
                firstname: userInfo.firstname,
                surname: userInfo.surname,
                roleid: role.value,
                permissions: userInfo.permissions,
            }
        )
            .then((response) => {
                if (response.data) {
                    toast.success('User Updated');
                    setSavedStates([...savedStates, 'User']);
                }
                setLoadingStates({ initial: false, user: false });
            })
            .catch((e) => {
                console.error(e);
                toast.error();
                setLoadingStates({ initial: false, user: true });
            });
    };

    const addUserToInstance = (instance, routingProfileId, securityProfileId) => {
        const request = {
            PhoneConfig: {
                AutoAccept: false,
                AfterContactWorkTimeLimit: 0,
                DeskPhoneNumber: '',
                PhoneType: 'SOFT_PHONE',
            },
            RoutingProfileId: routingProfileId,
            SecurityProfileIds: [securityProfileId],
            IdentityInfo: {
                FirstName: userInfo.firstname,
                LastName: userInfo.surname,
            },
            Username: userInfo.email,
        };
        CreateInstanceUser({ instanceId: instance.instanceid }, request)
            .then((response) => {
                if (response.data) {
                    addInstance(instance.instanceid, {
                        Id: response.data.UserId,
                        Arn: response.data.Arn,
                        ...request,
                    });
                    setModalControls({ open: false, currentInstance: {} });
                    toast.success(`User Added to Instance`);
                }
            })
            .catch((e) => {
                toast.error();
                console.error(e);
            });
    };

    if (loadingStates.initial) return <LoadingSpinner />;

    if (userLoadError) return <Generic />;

    return (
        <div>
            <Back to="/users">Back to List of Users</Back>
            <RowContainer spacing="large" className="pt-4">
                {/* User info column */}
                <ColContainer
                    yPadding="x-large"
                    xPadding="large"
                    cornerRadius="large"
                    spacing="large"
                    className="bg-white max-w-xl min-w-min flex-shrink-0 self-start"
                >
                    <RowContainer
                        spacing="large"
                        padding="large"
                        cornerRadius="large"
                        className="items-center bg-opacity-20 bg-user-background"
                    >
                        <Avatar
                            name={
                                userInfo.firstname
                                    ? `${userInfo.firstname} ${userInfo.surname}`
                                    : `${userInfo.email}`
                            }
                            heightClass="h-24"
                            widthClass="w-24"
                            textLarge={true}
                        />
                        <ColContainer spacing="medium" className="justify-evenly">
                            <input
                                disabled={!canEditUser}
                                className="appearance-none font-PoppinsMedium text-22/32 bg-transparent outline-none border-b border-transparent focus:border-brand-dark active:border-brand-dark"
                                value={`${userInfo.firstname} ${userInfo.surname}`}
                                onChange={(e) => parseName(e.target?.value)}
                            ></input>
                            <RowContainer className="items-center">
                                <Typography variant="heading4" color="grey-dark" className="w-16">
                                    Email
                                </Typography>
                                <Typography variant="heading2.2">{userInfo.email}</Typography>
                            </RowContainer>
                            <RowContainer className="items-center">
                                <Typography variant="heading4" color="grey-dark" className="w-16">
                                    Role
                                </Typography>
                                <div className="w-full">
                                    <SelectList
                                        disabled={!canEditUser}
                                        value={role}
                                        onChange={(e) => setRole(e)}
                                        options={roles}
                                    />
                                </div>
                            </RowContainer>
                        </ColContainer>
                    </RowContainer>
                    <ColContainer spacing="medium">
                        <RowContainer spacing="medium">
                            <Typography variant="heading2.2" className="w-60">
                                Set Routing Profile
                            </Typography>
                            <Checkbox
                                disabled={!canEditUser}
                                initialValue={userInfo.permissions.some((p) => p.id === 'irp')}
                                checked={userInfo.permissions.some((p) => p.id === 'irp')}
                                onChange={(event) => setPermission('irp', event)}
                            />
                        </RowContainer>
                        {/* <RowContainer spacing="medium">
                            <Typography variant="heading2.2" className="w-60">
                                Override Schedule
                            </Typography>
                            <Checkbox
                                disabled={!canEditUser}
                                initialValue={userInfo.permissions.some((p) => p.id === 'iorp')}
                                checked={userInfo.permissions.some((p) => p.id === 'iorp')}
                                onChange={(event) => setPermission('iorp', event)}
                            />
                        </RowContainer> */}
                        <RowContainer spacing="medium">
                            <Typography variant="heading2.2" className="w-60">
                                Pause/Stop Recording
                            </Typography>
                            <Checkbox
                                disabled={!canEditUser}
                                initialValue={userInfo.permissions.some((p) => p.id === 'ipsr')}
                                checked={userInfo.permissions.some((p) => p.id === 'ipsr')}
                                onChange={(event) => setPermission('ipsr', event)}
                            />
                        </RowContainer>
                        <RowContainer spacing="medium">
                            <Typography variant="heading2.2" className="w-60">
                                Toggle Auto Accept
                            </Typography>
                            <Checkbox
                                disabled={!canEditUser}
                                initialValue={userInfo.permissions.some((p) => p.id === 'iaa')}
                                checked={userInfo.permissions.some((p) => p.id === 'iaa')}
                                onChange={(event) => setPermission('iaa', event)}
                            />
                        </RowContainer>
                    </ColContainer>
                    <RowContainer spacing="medium">
                        {!savedStates.includes('User') ? (
                            <Button
                                type="primary"
                                disabled={loadingStates.user}
                                onClick={() => onSaveUserInfo()}
                            >
                                Save
                            </Button>
                        ) : (
                            <></>
                        )}
                        {canDeleteUser ? (
                            <Button
                                type="delete"
                                disabled={!canDeleteUser && loadingStates.user}
                                onClick={() => onDeleteUser()}
                            >
                                Delete User
                            </Button>
                        ) : (
                            <></>
                        )}
                    </RowContainer>
                </ColContainer>
                {/* Instances column */}
                <ColContainer spacing="medium" className="w-full">
                    {userInfo.instances.filter(instance => !!instance.user).map((instance) => {
                        return (
                            <InstancePanel
                                key={instance.instanceid}
                                instance={instance}
                                schedules={schedules
                                    .filter((s) => s.instanceid === instance.instanceid)
                                    .map((s) => ({ value: s.id, label: s.name }))}
                                setRoutingProfile={setRoutingProfile}
                                setSecurityProfile={setSecurityProfile}
                                setSchedule={setSchedule}
                                setAutoAccept={setAutoAccept}
                                setAfterCallAllowance={setAfterCallAllowance}
                                onSaveInstanceSettings={onSaveInstanceSettings}
                                setModalControls={setModalControls}
                                loadingStates={loadingStates}
                                canEditUser={canEditUser}
                                canDeleteUser={canDeleteUser}
                                onDeleteInstanceUser={onDeleteInstanceUser}
                            />
                        );
                    })}
                    {userInfo.instances.filter(instance => !instance.user).map((instance) => {
                        return (
                            <InstancePanel
                                key={instance.instanceid}
                                instance={instance}
                                schedules={schedules
                                    .filter((s) => s.instanceid === instance.instanceid)
                                    .map((s) => ({ value: s.id, label: s.name }))}
                                setRoutingProfile={setRoutingProfile}
                                setSecurityProfile={setSecurityProfile}
                                setSchedule={setSchedule}
                                setAutoAccept={setAutoAccept}
                                setAfterCallAllowance={setAfterCallAllowance}
                                onSaveInstanceSettings={onSaveInstanceSettings}
                                setModalControls={setModalControls}
                                loadingStates={loadingStates}
                                canEditUser={canEditUser}
                                canDeleteUser={canDeleteUser}
                                onDeleteInstanceUser={onDeleteInstanceUser}
                            />
                        );
                    })}
                </ColContainer>
            </RowContainer>
            {modalControls.open && canAddUser ? (
                <>
                    <AddUserToInstanceModal
                        currentInstance={modalControls.currentInstance}
                        onSubmit={(instance, routingProfileId, securityProfileId) =>
                            addUserToInstance(instance, routingProfileId, securityProfileId)
                        }
                        closeForm={() => setModalControls({ currentInstance: {}, open: false })}
                    />
                </>
            ) : (
                <></>
            )}
        </div>
    );
}

const InstancePanel = ({
    schedules,
    instance,
    setRoutingProfile,
    setSecurityProfile,
    setSchedule,
    setAutoAccept,
    setAfterCallAllowance,
    onSaveInstanceSettings,
    onDeleteInstanceUser,
    setModalControls,
    loadingStates,
    canEditUser,
    canDeleteUser,
}) => {
    const [collapsed, setCollapsed] = useState(false);
    const userExistsInAWSInstance = !!instance.user;
    const logo = instance.logo || EMPTY_IMAGE.base64;
    const icon = userExistsInAWSInstance ? 'chevron' : 'add';

    const routingProfiles = (instance?.routingprofiles || []).map((r) => ({
        value: r.Id,
        label: r.Name,
    }));

    const securityProfiles = (instance?.securityProfiles || []).map((r) => ({
        value: r.Id,
        label: r.Name,
        disabled: r.Name.toLowerCase() === 'admin',
    }));

    return (
        <ColContainer
            cornerRadius="large"
            xPadding="x-large"
            yPadding="large"
            className={`bg-white border-2 ${
                collapsed ? 'border-brand-light' : 'border-transparent'
            } transition-all`}
        >
            <RowContainer
                className="w-full items-left justify-between cursor-pointer"
                onClick={() =>
                    userExistsInAWSInstance
                        ? setCollapsed(!collapsed)
                        : setModalControls({ currentInstance: instance, open: true })
                }
            >
                <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 align-middle content-evenly grid">
                        <img
                            className="max-h-10 max-w-10 align-middle content-evenly text-center"
                            src={`data:image/png;base64,${logo}`}
                            alt={instance.company}
                        />
                    </div>
                    <div className="ml-4">
                        {instance.company} ({instance.project})
                    </div>
                </div>
                <div className="flex items-center">
                    <IconComponent
                        icon={icon}
                        rotate={collapsed ? 0 : 180}
                        colour={icon === 'add' && 'text-brand-main'}
                    />
                </div>
            </RowContainer>
            {!collapsed ? (
                <></>
            ) : !instance.user ? (
                <></>
            ) : (
                <ColContainer spacing="large" className="pt-6">
                    <ColContainer>
                        <Typography variant="body3">Schedule</Typography>
                        <SelectList
                            disabled={!canEditUser}
                            isClearable={true}
                            placeholder="None"
                            onChange={(e) => setSchedule(e?.value || '', instance)}
                            options={schedules}
                            value={schedules.find((r) => r.value === instance.scheduleid)}
                        />
                    </ColContainer>
                    <ColContainer>
                        <Typography variant="body3">Routing Profile*</Typography>
                        <SelectList
                            disabled={!canEditUser}
                            placeholder="None"
                            onChange={({ value }) => setRoutingProfile(value, instance)}
                            options={routingProfiles}
                            defaultValue={() =>
                                routingProfiles.find(
                                    (r) => r.value === instance.user.RoutingProfileId
                                )
                            }
                        />
                    </ColContainer>
                    <ColContainer>
                        <Typography variant="body3">Security Profile*</Typography>
                        <SelectList
                            disabled={!canEditUser}
                            secProfileList={true}
                            isClearable={false}
                            isMulti={true}
                            onChange={(values) => setSecurityProfile(values, instance)}
                            options={securityProfiles}
                            defaultValue={() =>
                                instance.user.SecurityProfileIds.map((i) => {
                                    return securityProfiles.find((p) => p.value === i);
                                })
                            }
                        />
                    </ColContainer>
                    <div className="grid grid-cols-2 pt-4">
                        <RowContainer className="items-center h-9">
                            <Typography variant="heading4" className="w-40">
                                After Call Allowance
                            </Typography>
                            <input
                                disabled={!canEditUser}
                                value={instance.user.PhoneConfig?.AfterContactWorkTimeLimit}
                                type="number"
                                min={0}
                                max={3600}
                                step={1}
                                onChange={(event) =>
                                    setAfterCallAllowance(event.target.value, instance)
                                }
                                className="appearance-none rounded-lg w-20 h-10 px-3 py-2 border border-grey-light text-black"
                            />
                        </RowContainer>
                        {/* <RowContainer className="items-center h-9">
                            <Typography variant="heading4" className="w-28">
                                Inbound Line
                            </Typography>
                            <Typography variant="heading3.2">the lord taketh a yeet</Typography>
                        </RowContainer> */}
                    </div>
                    <div className="grid grid-cols-2">
                        <RowContainer className="items-center h-9">
                            <Typography variant="heading4" className="w-40">
                                Auto Accept
                            </Typography>
                            <Checkbox
                                disabled={!canEditUser}
                                initialValue={instance.user.PhoneConfig?.AutoAccept}
                                checked={instance.user.PhoneConfig?.AutoAccept}
                                onChange={(event) => setAutoAccept(event.target.checked, instance)}
                            />
                        </RowContainer>
                        {/* <RowContainer className="items-center h-9">
                            <Typography variant="heading4" className="w-28">
                                Outbound Line
                            </Typography>
                            <Typography variant="heading3.2">the lord giveth a yeet</Typography>
                        </RowContainer> */}
                    </div>
                    <RowContainer spacing="medium">
                        <Button
                            disabled={!canEditUser || loadingStates.instance}
                            type="primary"
                            onClick={() => onSaveInstanceSettings(instance)}
                        >
                            Save
                        </Button>
                        {canDeleteUser ? (
                            <Button
                                disabled={loadingStates.instance && !canDeleteUser}
                                type="delete"
                                onClick={() => onDeleteInstanceUser(instance)}
                            >
                                Delete
                            </Button>
                        ) : (
                            <></>
                        )}
                    </RowContainer>
                </ColContainer>
            )}
        </ColContainer>
    );
};
